import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { View, Text, StyleSheet, TextInput, Pressable, ActivityIndicator, ScrollView, Platform } from 'react-native';
import { supabase } from '../lib/supabase';
import { formatDateOnly } from '../lib/dateUtils';

function defaultQuoteExpirationDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return formatDateOnly(d);
}

interface BidRequestContext {
  id: string;
  title: string;
  description: string | null;
  priority: string | null;
  status: string | null;
  home?: {
    city?: string | null;
    state?: string | null;
    market?: string | null;
  } | null;
}

type Step = 1 | 2 | 3;
type BidAmountMode = 'exact' | 'needs_visit';
interface BidAttachmentUpload {
  filePath: string;
  fileName: string;
  fileSize: number;
  fileType: string;
}

const MAX_ATTACHMENT_COUNT = 10;
const MAX_ATTACHMENT_FILE_SIZE = 200 * 1024 * 1024;

const ContractorBidPage = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token'), [searchParams]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [requestContext, setRequestContext] = useState<BidRequestContext | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [stepError, setStepError] = useState<string | null>(null);

  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [workDescription, setWorkDescription] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [bidAmountMode, setBidAmountMode] = useState<BidAmountMode>('exact');
  const [expiresAt, setExpiresAt] = useState(() => defaultQuoteExpirationDate());
  const [notes, setNotes] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);

  const resetBidForm = () => {
    setBusinessName('');
    setContactName('');
    setEmail('');
    setPhone('');
    setWebsite('');
    setWorkDescription('');
    setBidAmount('');
    setBidAmountMode('exact');
    setExpiresAt(defaultQuoteExpirationDate());
    setNotes('');
    setSelectedAttachments([]);
    setCurrentStep(1);
    setStepError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const validateAttachment = (file: File): string | null => {
    if (file.size <= 0 || file.size > MAX_ATTACHMENT_FILE_SIZE) {
      return `File "${file.name}" exceeds the 200MB limit.`;
    }
    return null;
  };

  const handleAttachmentChange = (event: any) => {
    const files = Array.from(event?.target?.files || []) as File[];
    if (files.length === 0) {
      return;
    }

    setStepError(null);
    const nextAttachments = [...selectedAttachments];
    for (const file of files) {
      if (nextAttachments.length >= MAX_ATTACHMENT_COUNT) {
        setStepError(`You can attach up to ${MAX_ATTACHMENT_COUNT} files.`);
        break;
      }

      const validationError = validateAttachment(file);
      if (validationError) {
        setStepError(validationError);
        continue;
      }

      nextAttachments.push(file);
    }

    setSelectedAttachments(nextAttachments);
    if (event?.target) {
      event.target.value = '';
    }
  };

  const removeSelectedAttachment = (index: number) => {
    setSelectedAttachments((prev) => prev.filter((_, attachmentIndex) => attachmentIndex !== index));
  };

  const uploadSelectedAttachments = async (): Promise<BidAttachmentUpload[]> => {
    if (!token || selectedAttachments.length === 0) {
      return [];
    }

    setUploadingAttachments(true);
    const uploadedAttachments: BidAttachmentUpload[] = [];

    try {
      for (const file of selectedAttachments) {
        const form = new FormData();
        form.append('token', token);
        form.append('file', file);

        const { data, error: invokeError } = await supabase.functions.invoke('upload-service-request-bid-attachment', {
          body: form,
        });

        if (invokeError) {
          throw new Error(invokeError.message || `Could not upload "${file.name}".`);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        if (!data?.attachment) {
          throw new Error(`Upload response for "${file.name}" was invalid.`);
        }

        uploadedAttachments.push(data.attachment as BidAttachmentUpload);
      }

      return uploadedAttachments;
    } finally {
      setUploadingAttachments(false);
    }
  };

  useEffect(() => {
    const loadRequestContext = async () => {
      setLoading(true);
      setError(null);
      setSubmissionComplete(false);

      try {
        if (!token) {
          setError('A bid access token is required.');
          return;
        }

        const { data, error: invokeError } = await supabase.functions.invoke('get-service-request-bid', {
          body: { token },
        });

        if (invokeError) {
          throw new Error(invokeError.message || 'Could not load bid page.');
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        setRequestContext(data?.request || null);
      } catch (loadError: any) {
        setError(loadError?.message || 'Could not load bid page.');
      } finally {
        setLoading(false);
      }
    };

    loadRequestContext();
  }, [token]);

  const handleSubmitBid = async () => {
    setSubmitting(true);
    setError(null);
    setStepError(null);
    setSubmissionComplete(false);

    try {
      if (!token) {
        throw new Error('A bid access token is required.');
      }

      if (!businessName.trim() || !contactName.trim() || !email.trim() || !phone.trim()) {
        throw new Error('Business name, contact name, email, and phone are required.');
      }

      if (bidAmountMode === 'exact' && !bidAmount.trim()) {
        throw new Error('Bid amount is required when using exact amount.');
      }

      const uploadedAttachments = await uploadSelectedAttachments();

      const { data, error: invokeError } = await supabase.functions.invoke('submit-service-request-bid', {
        body: {
          token,
          businessName,
          contactName,
          email,
          phone,
          website: website || null,
          serviceType: null,
          workDescription,
          bidAmount: bidAmountMode === 'needs_visit' ? null : bidAmount,
          bidAmountMode,
          expiresAt: expiresAt || null,
          notes: notes || null,
          attachments: uploadedAttachments,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'Could not submit bid.');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setSubmissionComplete(true);
      resetBidForm();
    } catch (submitError: any) {
      setError(submitError?.message || 'Could not submit bid.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnotherBid = () => {
    setError(null);
    setSubmissionComplete(false);
    resetBidForm();
  };

  const goToStep = (step: Step) => {
    setStepError(null);
    setCurrentStep(step);
  };

  const handleNext = () => {
    setStepError(null);
    if (currentStep === 1) {
      goToStep(2);
      return;
    }

    if (currentStep === 2) {
      if (!workDescription.trim()) {
        setStepError('Please describe the scope of work before continuing.');
        return;
      }
      if (bidAmountMode === 'exact' && !bidAmount.trim()) {
        setStepError('Please add a bid amount or choose "Need to visit to estimate".');
        return;
      }
      goToStep(3);
    }
  };

  const handleBack = () => {
    setStepError(null);
    if (currentStep === 2) {
      goToStep(1);
    } else if (currentStep === 3) {
      goToStep(2);
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#111827" />
      </View>
    );
  }

  if (error && !requestContext) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.card}>
        <View style={styles.heroBanner}>
          <Text style={styles.heroEyebrow}>Honeydone Contractor Portal</Text>
          <Text style={styles.title}>Share Your Best Bid</Text>
          <Text style={styles.subtitle}>
            Three quick steps. No account needed. Submit once and our team follows up fast.
          </Text>
        </View>

        {!submissionComplete && (
          <View style={styles.stepperRow}>
            {[1, 2, 3].map((step) => {
              const numericStep = step as Step;
              const isActive = currentStep === numericStep;
              const isComplete = currentStep > numericStep;
              return (
                <View key={step} style={styles.stepperItem}>
                  <View style={[styles.stepDot, isActive && styles.stepDotActive, isComplete && styles.stepDotComplete]}>
                    <Text style={[styles.stepDotText, (isActive || isComplete) && styles.stepDotTextActive]}>
                      {step}
                    </Text>
                  </View>
                  <Text style={[styles.stepLabel, isActive && styles.stepLabelActive]}>
                    {step === 1 ? 'Request' : step === 2 ? 'Bid' : 'Contact'}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {requestContext && (
          <View style={styles.requestInfo}>
            <Text style={styles.requestChip}>Service Request</Text>
            <Text style={styles.requestTitle}>{requestContext.title}</Text>
            {requestContext.description ? <Text style={styles.requestText}>{requestContext.description}</Text> : null}
            {(() => {
              const city = requestContext.home?.city;
              const state = requestContext.home?.state;
              const market = requestContext.home?.market;
              const location =
                city && state ? `${city}, ${state}` :
                city ? city :
                state ? state :
                market ? market :
                null;
              return location ? <Text style={styles.requestText}>{location}</Text> : null;
            })()}
          </View>
        )}

        {submissionComplete ? (
          <View style={styles.confirmationCard}>
            <Text style={styles.confirmationTitle}>Bid Received</Text>
            <Text style={styles.confirmationDescription}>
              Thanks for sending your bid. We received it and our team will review it and follow up with you soon.
            </Text>
            <Pressable style={styles.primaryButton} onPress={handleSubmitAnotherBid}>
              <Text style={styles.primaryButtonText}>Submit Another Bid</Text>
            </Pressable>
          </View>
        ) : currentStep === 1 && (
          <View style={styles.stepPanel}>
            <Text style={styles.stepTitle}>Review Request</Text>
            <Text style={styles.stepDescription}>
              Confirm the details above and continue when you are ready to share your estimate.
            </Text>
            <View style={styles.tipBox}>
              <Text style={styles.tipText}>
                Tip: provide a detailed scope in the next step to reduce back-and-forth and get approved faster.
              </Text>
            </View>
          </View>
        )}

        {!submissionComplete && currentStep === 2 && (
          <View style={styles.stepPanel}>
            <Text style={styles.stepTitle}>Bid Details</Text>
            <View style={styles.field}>
              <Text style={styles.label}>Work Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={workDescription}
                onChangeText={setWorkDescription}
                multiline
                numberOfLines={5}
              />
            </View>
            <View style={styles.amountModeRow}>
              <Pressable
                onPress={() => setBidAmountMode('exact')}
                style={[styles.amountModeButton, bidAmountMode === 'exact' && styles.amountModeButtonActive]}
              >
                <Text style={[styles.amountModeText, bidAmountMode === 'exact' && styles.amountModeTextActive]}>
                  Exact amount
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setBidAmountMode('needs_visit')}
                style={[styles.amountModeButton, bidAmountMode === 'needs_visit' && styles.amountModeButtonActive]}
              >
                <Text style={[styles.amountModeText, bidAmountMode === 'needs_visit' && styles.amountModeTextActive]}>
                  Need to visit to estimate
                </Text>
              </Pressable>
            </View>
            {bidAmountMode === 'exact' ? (
              <View style={styles.field}>
                <Text style={styles.label}>Bid Amount (USD) *</Text>
                <TextInput style={styles.input} value={bidAmount} onChangeText={setBidAmount} keyboardType="decimal-pad" />
              </View>
            ) : (
              <View style={styles.noticeBox}>
                <Text style={styles.noticeText}>
                  Your submission will be marked as requiring an on-site visit before final pricing.
                </Text>
              </View>
            )}
            <View style={styles.field}>
              <Text style={styles.label}>Quote Expiration Date</Text>
              {Platform.OS === 'web' ? (
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                  style={styles.webDateInput as any}
                />
              ) : (
                <TextInput style={styles.input} value={expiresAt} onChangeText={setExpiresAt} />
              )}
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Attachments (Optional)</Text>
              {Platform.OS === 'web' ? (
                <>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    onChange={handleAttachmentChange}
                    style={{ display: 'none' }}
                  />
                  <Pressable
                    style={styles.secondaryButton}
                    onPress={() => {
                      setStepError(null);
                      attachmentInputRef.current?.click();
                    }}
                    disabled={submitting || uploadingAttachments}
                  >
                    <Text style={styles.secondaryButtonText}>Choose Files</Text>
                  </Pressable>
                </>
              ) : (
                <Text style={styles.stepDescription}>Attachment uploads are currently available on web only.</Text>
              )}
              <Text style={styles.helperText}>
                Up to {MAX_ATTACHMENT_COUNT} files, 200MB each.
              </Text>
              {selectedAttachments.length > 0 ? (
                <View style={styles.attachmentList}>
                  {selectedAttachments.map((file, index) => (
                    <View key={`${file.name}-${file.size}-${index}`} style={styles.attachmentItem}>
                      <Text style={styles.attachmentText}>{file.name} ({formatFileSize(file.size)})</Text>
                      <Pressable
                        onPress={() => removeSelectedAttachment(index)}
                        disabled={submitting || uploadingAttachments}
                      >
                        <Text style={styles.removeAttachmentText}>Remove</Text>
                      </Pressable>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          </View>
        )}

        {!submissionComplete && currentStep === 3 && (
          <View style={styles.stepPanel}>
            <Text style={styles.stepTitle}>Business & Contact Info</Text>
            <View style={styles.field}>
              <Text style={styles.label}>Business Name *</Text>
              <TextInput style={styles.input} value={businessName} onChangeText={setBusinessName} />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Contact Name *</Text>
              <TextInput style={styles.input} value={contactName} onChangeText={setContactName} />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Email *</Text>
              <TextInput style={styles.input} value={email} onChangeText={setEmail} keyboardType="email-address" autoCapitalize="none" />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Phone *</Text>
              <TextInput style={styles.input} value={phone} onChangeText={setPhone} keyboardType="phone-pad" />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Website (Optional)</Text>
              <TextInput style={styles.input} value={website} onChangeText={setWebsite} autoCapitalize="none" />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Additional Notes (Optional)</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={notes}
                onChangeText={setNotes}
                multiline
                numberOfLines={4}
              />
            </View>
          </View>
        )}

        {stepError ? <Text style={styles.errorText}>{stepError}</Text> : null}
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        {submissionComplete ? <Text style={styles.successText}>We will follow up soon.</Text> : null}

        {!submissionComplete && (
          <View style={styles.footerActions}>
          {currentStep > 1 ? (
            <Pressable style={styles.secondaryButton} onPress={handleBack} disabled={submitting || uploadingAttachments}>
              <Text style={styles.secondaryButtonText}>Back</Text>
            </Pressable>
          ) : (
            <View />
          )}

          {currentStep < 3 ? (
            <Pressable style={styles.primaryButton} onPress={handleNext} disabled={submitting || uploadingAttachments}>
              <Text style={styles.primaryButtonText}>Continue</Text>
            </Pressable>
          ) : (
            <Pressable
              style={[styles.primaryButton, (submitting || uploadingAttachments) && styles.submitButtonDisabled]}
              onPress={handleSubmitBid}
              disabled={submitting || uploadingAttachments}
            >
              {submitting || uploadingAttachments ? (
                <View style={styles.submitRow}>
                  <ActivityIndicator size="small" color="#ffffff" />
                  <Text style={styles.primaryButtonText}>{uploadingAttachments ? 'Uploading files...' : 'Submitting...'}</Text>
                </View>
              ) : (
                <Text style={styles.primaryButtonText}>Submit Bid</Text>
              )}
            </Pressable>
          )}
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: '#f9fafb',
  },
  container: {
    padding: 24,
    backgroundColor: '#f9fafb',
    minHeight: '100%' as any,
  },
  card: {
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#dbeafe',
    padding: 22,
    gap: 18,
    shadowColor: '#2563eb',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.09,
    shadowRadius: 20,
    elevation: 4,
  },
  heroBanner: {
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#bfdbfe',
    borderRadius: 12,
    padding: 14,
    gap: 6,
  },
  heroEyebrow: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#1d4ed8',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    color: '#111827',
  },
  subtitle: {
    fontSize: 14,
    color: '#4b5563',
  },
  stepperRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  stepperItem: {
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  stepDot: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  stepDotActive: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
  },
  stepDotComplete: {
    backgroundColor: '#059669',
    borderColor: '#059669',
  },
  stepDotText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6b7280',
  },
  stepDotTextActive: {
    color: '#ffffff',
  },
  stepLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  stepLabelActive: {
    color: '#111827',
    fontWeight: '700',
  },
  requestInfo: {
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 12,
    gap: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  requestChip: {
    alignSelf: 'flex-start',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    color: '#1d4ed8',
    backgroundColor: '#dbeafe',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  requestText: {
    fontSize: 13,
    color: '#374151',
  },
  field: {
    gap: 6,
  },
  stepPanel: {
    gap: 14,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  stepDescription: {
    fontSize: 13,
    color: '#4b5563',
  },
  tipBox: {
    borderWidth: 1,
    borderColor: '#c7d2fe',
    backgroundColor: '#eef2ff',
    borderRadius: 10,
    padding: 10,
  },
  tipText: {
    fontSize: 12,
    color: '#3730a3',
  },
  confirmationCard: {
    borderWidth: 1,
    borderColor: '#86efac',
    backgroundColor: '#f0fdf4',
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  confirmationTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#166534',
  },
  confirmationDescription: {
    fontSize: 14,
    color: '#14532d',
    lineHeight: 20,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  helperText: {
    fontSize: 12,
    color: '#6b7280',
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#111827',
  },
  amountModeRow: {
    flexDirection: 'row',
    gap: 10,
  },
  amountModeButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
    alignItems: 'center',
  },
  amountModeButtonActive: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  amountModeText: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '600',
    textAlign: 'center',
  },
  amountModeTextActive: {
    color: '#1d4ed8',
  },
  noticeBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#a7f3d0',
    backgroundColor: '#ecfdf5',
    padding: 10,
  },
  noticeText: {
    fontSize: 12,
    color: '#065f46',
  },
  textArea: {
    minHeight: 96,
    textAlignVertical: 'top',
  },
  webDateInput: {
    padding: '10px 12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: '#ffffff',
    width: '100%',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  footerActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
    gap: 12,
  },
  primaryButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1d4ed8',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 18,
    minWidth: 132,
  },
  secondaryButton: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderWidth: 1,
    borderColor: '#d1d5db',
    minWidth: 96,
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  secondaryButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '600',
  },
  attachmentList: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    overflow: 'hidden',
  },
  attachmentItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  attachmentText: {
    flex: 1,
    fontSize: 13,
    color: '#374151',
  },
  removeAttachmentText: {
    fontSize: 13,
    color: '#dc2626',
    fontWeight: '600',
  },
  errorText: {
    fontSize: 13,
    color: '#dc2626',
  },
  successText: {
    fontSize: 13,
    color: '#059669',
  },
});

export default ContractorBidPage;
